import { makeAutoObservable } from "mobx";
import { identity, isError } from "lodash";
import { ApiService } from "../api";
import { Language, languageLabels, supportedBackendLanguages } from "../i18n/languages";
import { RadioFieldModel, StringFieldModel } from "../common/commonForms";
import { TMessage } from "../i18n/tt";
import { InvitationType } from "../types/Invitation";
import AlertMessageStore from "./AlertMessageStore";
import { emailValidator } from "../common/common";
import { AIClient } from "../ai/AIAssistant";
import { LocalizedTextEditorModel } from "../text/localizedTextEditorDomain";

export type SendInvitationDialogParams = {
  invitationType: InvitationType;
  organisationNumber: string;
  lang: Language;
  title: TMessage;
  description?: TMessage;
  adminInfo?: TMessage;
  shareholderId?: number;
  roleId?: string;
  email?: string;
  customRoleName?: string;
};

export class SendInvitationDialogStore {
  state: "input" | "loading" | "error" = "input";
  email: StringFieldModel;
  language: RadioFieldModel<Language>;

  invitationType: InvitationType;
  apiService: ApiService;
  messageStore: AlertMessageStore;
  organisationNumber: string;
  title: TMessage;
  adminInfo?: TMessage;
  description?: TMessage;
  shareholderId?: number;
  roleId?: string;

  error: string | null = null;
  onClose: () => void;
  isPersonalNoteEnabled: boolean = false;
  personalNote: LocalizedTextEditorModel;
  commonEmailText: TMessage;

  constructor(
    params: SendInvitationDialogParams,
    apiService: ApiService,
    messageStore: AlertMessageStore,
    onClose: () => void,
    aiClient: AIClient,
    commonEmailText: TMessage,
  ) {
    this.apiService = apiService;
    this.messageStore = messageStore;
    this.onClose = onClose;

    this.invitationType = params.invitationType;
    this.organisationNumber = params.organisationNumber;
    this.title = params.title;
    this.adminInfo = params.adminInfo;
    this.description = params.description;
    this.shareholderId = params.shareholderId;
    this.roleId = params.roleId;

    this.email = new StringFieldModel({
      value: params.email,
      required: true,
      validator: emailValidator,
    });
    this.language = new RadioFieldModel<Language>({
      value: supportedBackendLanguages.find((d) => d === params.lang) ?? Language.EN,
      options: supportedBackendLanguages,
      idFn: identity,
      labelFn: (l) => ({ key: languageLabels[l] }),
    });
    this.personalNote = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { translated: "" },
      aiClient,
    });
    this.commonEmailText = commonEmailText;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setState(state: "input" | "loading" | "error") {
    this.state = state;
  }

  setError(error: unknown) {
    this.error = isError(error) ? error.message : "Unknown error";
  }

  validate() {
    return this.email.validate();
  }

  get closeLabel() {
    return this.state === "input" ? "invite-user.cancel" : "invite-user.close";
  }

  get showSubmitButton() {
    return this.state !== "error";
  }

  get isLoading() {
    return this.state === "loading";
  }

  togglePersonalNote() {
    this.isPersonalNoteEnabled = !this.isPersonalNoteEnabled;
    if (!this.isPersonalNoteEnabled && this.personalNote?.textModels?.length > 0) {
      this.personalNote.textModels[0].model.setAndCommitInputValue("");
    }
  }

  *submitInviteUserDialog() {
    if (!this.validate()) {
      return;
    }
    this.setState("loading");
    try {
      yield this.apiService.sendInvitation({
        invitationType: this.invitationType,
        orgNo: this.organisationNumber,
        email: this.email.validatedValue,
        language: this.language.value,
        shareholderId: this.shareholderId,
        roleId: this.roleId,
        personalNoteText: this.isPersonalNoteEnabled
          ? this.personalNote.textModels[0].model.validatedValue
          : undefined,
      });
      this.onClose();
      this.messageStore.showSuccess({ key: "invite-user.success-message" });
    } catch (e) {
      this.setState("error");
      this.setError(e);
    }
  }
}
